import React, { memo } from "react";
import SlaMetricCard from "./SlaMetricCard";
import { SlaSectionType, SlaSummary } from "../types";

interface Props {
  summary: SlaSummary | null;
  // The lifecycle currently selected in the UI. Needed independently of
  // `summary` because `summary` is null while loading / before the first
  // response — without this the correct card set (3 vs 4) can't be chosen
  // during that window (api-contract §5.2/§5.3, react-frontend-tasks F8).
  lifecycle: SlaSectionType;
}

// api-contract §5.2/§5.3 / react-frontend-tasks F8:
// - lifecycle "open": Upcoming / At Risk / Breached (3 cards).
// - lifecycle "completed": Ahead / On Time / At Risk / Breached (4 cards).
// Backend-computed percentages are passed through directly — the frontend
// does NOT compute them (api-contract §5.4).
const SlaMetricCards = ({ summary, lifecycle }: Props) => {
  const openSummary = summary && summary.lifecycle === "open" ? summary : null;
  const completedSummary = summary && summary.lifecycle === "completed" ? summary : null;

  if (lifecycle === "completed") {
    return (
      <div className="flex gap-4 flex-wrap mb-6">
        <SlaMetricCard
          title="Ahead"
          count={completedSummary?.ahead_count ?? 0}
          percentage={completedSummary?.ahead_pct}
          subtitle="Closed ahead of due date"
          variant="primary"
        />
        <SlaMetricCard
          title="On Time"
          count={completedSummary?.on_time_count ?? 0}
          percentage={completedSummary?.on_time_pct}
          subtitle="Closed on the due date"
          variant="primary"
        />
        <SlaMetricCard
          title="At Risk"
          count={completedSummary?.at_risk_count ?? 0}
          percentage={completedSummary?.at_risk_pct}
          subtitle="Closed late, within grace"
          variant="warning"
        />
        <SlaMetricCard
          title="Breached"
          count={completedSummary?.breached_count ?? 0}
          percentage={completedSummary?.breached_pct}
          subtitle="Closed beyond SLA"
          variant="danger"
        />
      </div>
    );
  }

  return (
    <div className="flex gap-4 flex-wrap mb-6">
      <SlaMetricCard
        title="Upcoming"
        count={openSummary?.upcoming_count ?? 0}
        percentage={openSummary?.upcoming_pct}
        subtitle="Tasks within target window"
        variant="primary"
      />
      <SlaMetricCard
        title="At Risk"
        count={openSummary?.at_risk_count ?? 0}
        percentage={openSummary?.at_risk_pct}
        subtitle="Tasks at risk"
        variant="warning"
      />
      <SlaMetricCard
        title="Breached"
        count={openSummary?.breached_count ?? 0}
        percentage={openSummary?.breached_pct}
        subtitle="Tasks overdue SLA"
        variant="danger"
      />
    </div>
  );
};

export default memo(SlaMetricCards);
